/**
 * Edit Quiz page - manage quiz questions
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import AddQuestionDialog from '../components/AddQuestionDialog';
import DeleteQuestionDialog from '../components/DeleteQuestionDialog';
import EditQuestionDialog from '../components/EditQuestionDialog';
import EditTitleDialog from '../components/EditTitleDialog';
import EditIcon from '../components/EditIcon';
import { QuestionType } from '../db/schema';
import './edit.css';

interface Question {
    id: number;
    quizId: number;
    questionType: number;
    question: string;
    answer: string;
    answer_choices_json: string;
    answerExplanation?: string | null;
}

interface Quiz {
    id: number;
    title: string;
    description: string;
    userId: number;
    isPublic: number;
}

const questionTypeLabels: Record<QuestionType, string> = {
    [QuestionType.SHORT_ANSWER]: 'Short Answer',
    [QuestionType.MULTIPLE_CHOICE]: 'Multiple Choice',
    [QuestionType.TRUE_FALSE]: 'True/False',
};

export default function EditQuiz() {
    const { id } = useParams<{ id: string }>();
    const quizId = Number(id);

    const [quiz, setQuiz] = useState<Quiz | null>(null);
    const [questions, setQuestions] = useState<Question[]>([]);
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isTitleEditDialogOpen, setIsTitleEditDialogOpen] = useState(false);
    const [questionToDelete, setQuestionToDelete] = useState<Question | null>(null);
    const [questionToEdit, setQuestionToEdit] = useState<Question | null>(null);

    const fetchQuizData = async () => {
        try {
            const response = await fetch(`/api/quizzes/${quizId}`);
            if (response.ok) {
                const data = await response.json();
                setQuiz(data.quiz);
                setQuestions(data.questions);
            }
        } catch (error) {
            console.error('Failed to fetch quiz data:', error);
        }
    };

    useEffect(() => {
        fetchQuizData();
    }, [quizId]);

    const handleAddQuestionClose = () => {
        setIsAddDialogOpen(false);
        fetchQuizData();
    };

    const handleDeleteClick = (question: Question) => {
        setQuestionToDelete(question);
        setIsDeleteDialogOpen(true);
    };

    const handleDeleteClose = () => {
        setIsDeleteDialogOpen(false);
        setQuestionToDelete(null);
        fetchQuizData();
    };

    const handleEditClick = (question: Question) => {
        setQuestionToEdit(question);
        setIsEditDialogOpen(true);
    };

    const handleEditClose = () => {
        setIsEditDialogOpen(false);
        setQuestionToEdit(null);
        fetchQuizData();
    };

    const handleTitleEditClose = () => {
        setIsTitleEditDialogOpen(false);
        fetchQuizData();
    };

    return (
        <main className="editQuizMain">
            <div className="editQuizHeader">
                <Link to="/" className="backLink">
                    ← Back to Quizzes
                </Link>
                <div className="quizTitleContainer">
                    <h2>{quiz?.title || 'Loading...'}</h2>
                    <button
                        className="editTitleButton"
                        onClick={() => setIsTitleEditDialogOpen(true)}
                        title="Edit Quiz Title"
                    >
                        <EditIcon />
                    </button>
                </div>
                <p className="quizDescription">{quiz?.description}</p>
            </div>

            <div className="questionsSection">
                <div className="questionsSectionHeader">
                    <h3>Questions</h3>
                    <button className="addQuestionButton" onClick={() => setIsAddDialogOpen(true)}>
                        + Add Question
                    </button>
                </div>

                {questions.length === 0 ? (
                    <p className="noQuestions">
                        No questions yet. Click "Add Question" to get started!
                    </p>
                ) : (
                    <div className="questionsList">
                        {questions.map((question, index) => (
                            <div key={question.id} className="questionItem">
                                <div className="questionNumber">{index + 1}.</div>
                                <div className="questionContent">
                                    <div className="questionTextRow">
                                        <p className="questionText">{question.question}</p>
                                        <span className="questionTypeTag">
                                            {questionTypeLabels[question.questionType as QuestionType]}
                                        </span>
                                    </div>
                                    <p className="answerText">Answer: {question.answer}</p>
                                </div>
                                <div className="questionActions">
                                    <button
                                        className="editButton"
                                        onClick={() => handleEditClick(question)}
                                        title="Edit Question"
                                    >
                                        <EditIcon />
                                    </button>
                                    <button
                                        className="deleteButton"
                                        onClick={() => handleDeleteClick(question)}
                                        title="Delete Question"
                                    >
                                        <svg viewBox="0 0 24 24" fill="currentColor">
                                            <path
                                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                fill="none"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <AddQuestionDialog isOpen={isAddDialogOpen} onClose={handleAddQuestionClose} quizId={quizId} />

            {questionToDelete && (
                <DeleteQuestionDialog
                    isOpen={isDeleteDialogOpen}
                    onClose={handleDeleteClose}
                    questionId={questionToDelete.id}
                    questionText={questionToDelete.question}
                />
            )}

            {questionToEdit && (
                <EditQuestionDialog
                    isOpen={isEditDialogOpen}
                    onClose={handleEditClose}
                    questionId={questionToEdit.id}
                    initialQuestion={questionToEdit.question}
                    initialAnswer={questionToEdit.answer}
                    initialQuestionType={questionToEdit.questionType}
                    initialAnswerChoices={
                        questionToEdit.answer_choices_json
                            ? JSON.parse(questionToEdit.answer_choices_json)
                            : []
                    }
                    initialAnswerExplanation={questionToEdit.answerExplanation}
                />
            )}

            {quiz && (
                <EditTitleDialog
                    isOpen={isTitleEditDialogOpen}
                    onClose={handleTitleEditClose}
                    quizId={quizId}
                    initialTitle={quiz.title}
                    initialDescription={quiz.description}
                    initialIsPublic={quiz.isPublic === 1}
                />
            )}
        </main>
    );
}
